import { useCallback, useState } from 'react';
import { Refinement } from 'react-instantsearch-core';
import { RangeRefinement } from '~/features/productsList/models';
import { useFranchise, useGtm } from '~/shared/utils';
import { TrackedFilters } from '~/shared/utils/gtm/lib/events/trackFilterSelectedEvent';

type FilterRefinement = Refinement & {
    currentRefinement: string | string[] | RangeRefinement | undefined;
};

const rangeSeparator = '-';

export const useFilterTracker = () => {
    const { trackFilterSelected } = useGtm();
    const { activeMarket } = useFranchise();
    const [previousItems, setPreviousItems] = useState<FilterRefinement[] | null>(null);

    const getFilterName = useCallback((name: string) => {
        // Get the name of the filter as a usable key that matches the event type.
        // Also remove the prefix "Mother" from e.g. "MotherColour".
        // "variants.attributes.MotherColour" → "colour"
        return (name.split('.').pop() || name)
            .replace('Mother', '')
            .toLowerCase() as keyof TrackedFilters;
    }, []);

    const trackFilters = useCallback(
        (items: FilterRefinement[]) => {
            const filtersToTrack: TrackedFilters = {};

            // If a value has been cleared entirely, set it to 'cleared'
            previousItems?.forEach((prevItem) => {
                const isCleared = !items.some((item) => item.id === prevItem.id);
                if (isCleared) {
                    filtersToTrack[getFilterName(prevItem.id)] = 'cleared';
                }
            });

            items?.forEach((item) => {
                const { currentRefinement: facets } = item;
                const filterName = getFilterName(item.id);
                let filterValue: string;
                if (filterName === 'price') {
                    filterValue = `${(facets as RangeRefinement).min}${rangeSeparator}${
                        (facets as RangeRefinement).max
                    }`;
                } else if (Array.isArray(facets)) {
                    filterValue = facets.join(', ');
                } else {
                    filterValue = facets;
                }

                filtersToTrack[filterName] = filterValue;
            });
            if (Object.values(filtersToTrack).some((val) => val)) {
                trackFilterSelected(
                    filtersToTrack,
                    filtersToTrack.price?.includes(rangeSeparator)
                        ? activeMarket?.currency
                        : undefined
                );
            }
            setPreviousItems(items);
        },
        [activeMarket?.currency, getFilterName, previousItems, trackFilterSelected]
    );
    return { trackFilters };
};
